import { PaginateQuery } from "../../interfaces";
import { Paginated } from "../../models";
import { baseApi } from "./baseApi";
import { buildPaginationQueryString } from "./queryHelpers";

export interface TesterUser {
  id: string;
  name?: { en?: string; ar?: string };
  phone?: string;
  role?: string;
  isTester?: boolean;
}

export interface TesterFeedbackMedia {
  id: string;
  type?: string;
  path?: string;
  videoUrl?: string;
  thumbnail?: string;
  name?: string;
}

export interface TesterFeedback {
  id: string;
  message: string;
  createdAt: string;
  isFixed?: boolean;
  fixedAt?: string | null;
  platform?: string | null;
  osName?: string | null;
  osVersion?: string | null;
  appVersion?: string | null;
  appBuild?: string | null;
  deviceModel?: string | null;
  deviceBrand?: string | null;
  deviceId?: string | null;
  deviceName?: string | null;
  bundleId?: string | null;
  isTablet?: boolean | null;
  isEmulator?: boolean | null;
  locale?: string | null;
  timezone?: string | null;
  screenWidth?: number | null;
  screenHeight?: number | null;
  orientation?: string | null;
  extra?: Record<string, unknown> | null;
  user?: TesterUser;
  media?: TesterFeedbackMedia[];
}

export interface TesterFeedbackFacets {
  platforms: string[];
  appVersions: string[];
  osVersions: string[];
}

export const testerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTesters: builder.query<Paginated<TesterUser>, PaginateQuery>({
      query: (data) => ({
        url: buildPaginationQueryString(data, "/api/tester"),
      }),
      providesTags: ["Testers"],
    }),
    getTesterFeedback: builder.query<
      Paginated<TesterFeedback>,
      PaginateQuery
    >({
      query: (data) => ({
        url: buildPaginationQueryString(data, "/api/tester/feedback"),
      }),
      providesTags: ["TesterFeedback"],
    }),
    getTesterFeedbackFacets: builder.query<TesterFeedbackFacets, void>({
      query: () => ({ url: "/api/tester/feedback/facets" }),
      providesTags: ["TesterFeedback"],
    }),
    setTesterFeedbackFixed: builder.mutation<
      { id: string; isFixed: boolean },
      { id: string; isFixed: boolean }
    >({
      query: ({ id, isFixed }) => ({
        url: `/api/tester/feedback/${id}/fixed`,
        method: "PATCH",
        body: { isFixed },
      }),
      invalidatesTags: ["TesterFeedback"],
    }),
    searchTesterUsers: builder.query<TesterUser[], string>({
      query: (search) => ({
        url: `/api/tester/search?search=${encodeURIComponent(search)}`,
      }),
    }),
    addTester: builder.mutation<void, string>({
      query: (userId) => ({
        url: "/api/tester",
        method: "POST",
        body: { userId },
      }),
      invalidatesTags: ["Testers"],
    }),
    removeTester: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/api/tester/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Testers"],
    }),
  }),
});

export const {
  useGetTestersQuery,
  useGetTesterFeedbackQuery,
  useGetTesterFeedbackFacetsQuery,
  useSetTesterFeedbackFixedMutation,
  useLazySearchTesterUsersQuery,
  useAddTesterMutation,
  useRemoveTesterMutation,
} = testerApi;
